import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@carbon/react';
import { Location } from '@carbon/react/icons';
import { appStore } from 'components/map';
import {
  PANEL_OFFSET_PX,
  useAnchoredPanel,
} from 'lib/hooks/use-anchored-panel';
import {
  cardHostAtom,
  closeCoordinatesAtom,
  coordinatesOpenAtom,
  openCoordinatesAtom,
} from './atoms';
import { CoordinatesCard } from './coordinates-card';
import styles from './coordinate-pins.module.scss';

/**
 * Keeps the card beside its button, its top level with the button's. The card lives on
 * `document.body`, so it can run past the map and whatever frames it; this places it
 * as the window resizes or scrolls, and caps its height at the window's bottom, where
 * its pin list scrolls instead.
 *
 * @param {boolean} open - Whether the card is showing.
 * @return {object} Refs for the button's wrapper and the card.
 */
const useCardBesideButton = (open: boolean) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const card = cardRef.current;
    if (!anchor || !card) return;
    const rect = anchor.getBoundingClientRect();
    card.style.top = `${rect.top}px`;
    card.style.right = `${window.innerWidth - rect.left + PANEL_OFFSET_PX}px`;
    card.style.maxHeight = `${window.innerHeight - rect.top - PANEL_OFFSET_PX}px`;
  }, []);
  useAnchoredPanel(open, place);

  return { anchorRef, cardRef };
};

export interface CoordinatesButtonProps {
  /** This map's button, so only one of the maps on screen draws the card. */
  host: symbol;
  /** The control-stack button class, so this matches the column it sits in. */
  className?: string;
  /** Called when the card opens, so the host can put its own panels away. */
  onOpen?: () => void;
}

/**
 * The control-stack button that toggles the Coordinates card, and the card itself,
 * beside the button.
 *
 * @param {CoordinatesButtonProps} props - Component props.
 * @return {JSX.Element} The button, with the card when it is open.
 */
export function CoordinatesButton({
  host,
  className,
  onOpen,
}: CoordinatesButtonProps): JSX.Element {
  const open = useAtomValue(coordinatesOpenAtom, appStore);
  const cardHost = useAtomValue(cardHostAtom, appStore);
  const openHere = useSetAtom(openCoordinatesAtom, appStore);
  const close = useSetAtom(closeCoordinatesAtom, appStore);
  const showCard = open && cardHost === host;
  const { anchorRef, cardRef } = useCardBesideButton(showCard);

  return (
    <div ref={anchorRef} className={styles.control}>
      <Button
        kind={showCard ? 'secondary' : 'ghost'}
        size="sm"
        hasIconOnly
        renderIcon={Location}
        iconDescription="Drop Pins"
        tooltipAlignment="center"
        tooltipPosition="left"
        className={className}
        aria-pressed={showCard}
        onClick={() => {
          // This map's card closes; another map's moves here.
          if (showCard) {
            close();
            return;
          }
          openHere(host);
          onOpen?.();
        }}
      />
      {showCard &&
        createPortal(
          <div
            ref={cardRef}
            className={styles.cardFrame}
            data-testid="coordinates-card-frame"
          >
            <CoordinatesCard />
          </div>,
          document.body
        )}
    </div>
  );
}
